//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import Text from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

interface NestedCheckboxGroupProps {
  children?: ReactNode;
  endComponent?: ReactNode;
}

export const NestedCheckboxGroup = ({
  children,
  endComponent,
}: NestedCheckboxGroupProps) => {
  return (
    <StyledNestedGroup>
      {children}

      {isDefined(endComponent) && (
        <StyledCheckboxItem hasChildren={false}>
          {endComponent}
        </StyledCheckboxItem>
      )}
    </StyledNestedGroup>
  );
};

interface ItemProps {
  isIndeterminate: boolean;
  isSelected: boolean;
  label: string;
  subItems?: ItemProps[];
}

interface CheckboxItemProps<T extends ItemProps> {
  handleChangeSelect: (updatedItemState: T) => void;
  item: T;
  rightComponent?: ReactNode;
  shouldShowCheckbox?: boolean;
}

const LABEL_MIXIN = Mixins.Text.Caption;

interface NestedCheckboxItemWithChildrenProps<T extends ItemProps> {
  children: ReactNode;
  handleToggleShowSubItems: () => void;
  shouldShowSubItems: boolean;
}

interface NestedCheckboxItemWithoutChildrenProps {
  children?: undefined;
  handleToggleShowSubItems?: undefined;
  shouldShowSubItems?: undefined;
}

type NestedCheckboxItemChildrenProps<T extends ItemProps> =
  | NestedCheckboxItemWithChildrenProps<T>
  | NestedCheckboxItemWithoutChildrenProps;

type NestedCheckboxItemProps<T extends ItemProps> = CheckboxItemProps<T> &
  NestedCheckboxItemChildrenProps<T>;

export const NestedCheckboxItem = <T extends ItemProps>({
  children,
  handleChangeSelect,
  handleToggleShowSubItems,
  item,
  rightComponent,
  shouldShowCheckbox = true,
  shouldShowSubItems = true,
}: NestedCheckboxItemProps<T>) => {
  const { isIndeterminate, isSelected, label } = item;

  const hasChildren = isDefined(item.subItems) && item.subItems.length > 0;

  const handleChange = () => {
    const updatedSelectedValue = !isSelected;

    const changeIsSelect = (item: ItemProps) => {
      // Select all descendants and set isIndeterminate to true if updatedSelectedValue === true. Unselect all descendants and set isIndeterminate to false if updatedSelectedValue === false.
      item.isIndeterminate = updatedSelectedValue;
      item.isSelected = updatedSelectedValue;

      item.subItems?.forEach((subItem) => changeIsSelect(subItem));
    };

    changeIsSelect(item);

    handleChangeSelect(item);
  };

  return (
    <StyledCheckboxItem hasChildren={hasChildren}>
      <ItemWrapper>
        {hasChildren && (
          <ShowSubItemsIconWrapper onClick={handleToggleShowSubItems}>
            <ToggleIcon isCollapsed={!shouldShowSubItems} name='chevron-down' />
          </ShowSubItemsIconWrapper>
        )}

        <CheckboxLabel
          handleChange={handleChange}
          isIndeterminate={isIndeterminate}
          isSelected={isSelected}
          label={label}
          shouldShowCheckbox={shouldShowCheckbox}
        />

        {isDefined(rightComponent) ? rightComponent : undefined}
      </ItemWrapper>

      {shouldShowSubItems && children}
    </StyledCheckboxItem>
  );
};

const isOverflow = (element: HTMLElement | null): boolean => {
  return (
    !!element?.firstElementChild &&
    element.clientWidth <= element.firstElementChild.clientWidth
  );
};

interface CheckboxLabelProps {
  handleChange: () => void;
  isIndeterminate: boolean;
  isSelected: boolean;
  label: string;
  shouldShowCheckbox: boolean;
}

const CheckboxLabel = ({
  handleChange,
  isIndeterminate,
  isSelected,
  label,
  shouldShowCheckbox,
}: CheckboxLabelProps) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState<boolean>(false);

  useEffect(() => {
    setShouldShowTooltip(isOverflow(textRef.current));
  }, [textRef]);

  return (
    <SmallTooltip
      content={label}
      interactive={false}
      isDisabled={!shouldShowTooltip}
      target={
        shouldShowCheckbox ? (
          <StyledLabeledCheckbox
            ellipsize={1}
            handleClick={handleChange}
            isChecked={isSelected}
            isIndeterminate={isIndeterminate}
            label={label}
            labelMixinOverride={LABEL_MIXIN}
            textRef={textRef}
          />
        ) : (
          <Text
            ellipsize={1}
            height={16}
            mixin={LABEL_MIXIN}
            offset={0}
            textRef={textRef}
          >
            {label}
          </Text>
        )
      }
    />
  );
};

const StyledNestedGroup = styled(DropdownMenu.Group)(
  ({ theme }) => `
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
  padding-top: ${theme.spacings[2]};
  padding-left: ${theme.spacings[4]};
`,
);

const StyledCheckboxItem = styled.div<{ hasChildren: boolean }>(
  ({ hasChildren, theme }) => `
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: baseline;
  min-height: fit-content;
  padding-left: ${hasChildren ? 0 : theme.spacings[4]};
  padding-right: 0;

  &:hover {
    background-color: unset;
  };
`,
);

const ItemWrapper = styled.div(
  ({ theme }) => `
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacings[2]};

  & > span {
    width: 100%;
  }
`,
);

const ShowSubItemsIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ToggleIcon = styled(Icon)<{ isCollapsed: boolean }>(
  ({ isCollapsed }) => `
    transform: rotate(${isCollapsed ? '-90deg' : 0});
    transition: transform 200ms ease;
  `,
);

const StyledLabeledCheckbox = styled(LabeledCheckbox)`
  width: 100%;

  label {
    width: 100%;
  }
`;
